/**
 * Culture and borders (ADR-0057).
 *
 * Every city adds its buildings' culture to its own running total each turn. The total
 * pushes the city's border out a ring at a time as it passes the thresholds in
 * `rules.culture`, and where two borders overlap the tile goes to whichever city has
 * the most influence on it: (culture + 1) / distance². So a cultured city can press a
 * frontier back without a war — but never into a city's own first ring, which always
 * stays with it, because a city that loses the tiles beside it starves for reasons the
 * player cannot see or answer.
 *
 * Ownership is recomputed from the cities every turn rather than patched tile by tile,
 * so it is always exactly what the rule says. A tie leaves the tile where it was.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { hexDistance, hexSpiral } from '../hex/index.js';
import { hexIndex, indexToHex, tileCount, tileIndex } from '../state/grid.js';
import { NONE, type City, type GameState, type Id } from '../state/types.js';
import { inDisorder } from '../cities/mood.js';
import { effectTotal } from '../cities/production.js';
import { civEffectTotal } from '../cities/wonders.js';
import { governmentEffects } from '../government/index.js';

/** Culture a city earns this turn: its buildings, plus wonders that reach every city. */
export function culturePerTurn(state: GameState, content: Content, city: City): number {
  const raw = effectTotal(content, city, 'culture') + civEffectTotal(state, content, city.owner, 'culture');
  const percent = governmentEffects(state, content, city.owner).culturePercent;
  return percent === 0 ? raw : Math.max(0, Math.floor((raw * (100 + percent)) / 100));
}

/** The border radius a culture total has earned. */
export function borderRadiusFor(content: Content, culture: number): number {
  let radius = content.rules.cities.startingBorderRadius;
  for (const threshold of content.rules.culture.borderThresholds) if (culture >= threshold) radius++;
  return radius;
}

/** The culture total at which this city's border next grows, or null if it never will. */
export function nextBorderThreshold(content: Content, culture: number): number | null {
  for (const threshold of content.rules.culture.borderThresholds) if (culture < threshold) return threshold;
  return null;
}

/** A civilisation's culture: the sum of its cities' totals, and what they add a turn. */
export function civCulture(state: GameState, content: Content, civId: Id): { total: number; perTurn: number } {
  let total = 0;
  let perTurn = 0;
  for (const city of Object.values(state.cities)) {
    if (city.owner !== civId) continue;
    total += city.culture;
    if (!inDisorder(state, content, city)) perTurn += culturePerTurn(state, content, city);
  }
  return { total, perTurn };
}

/**
 * Add a turn's culture to each of a civilisation's cities. A city in disorder adds
 * nothing, as it produces nothing else. Borders never shrink: a radius, once earned,
 * stays — the threshold check only ever raises it.
 */
export function advanceCulture(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  const cities = Object.values(state.cities)
    .filter((c) => c.owner === civId)
    .sort((a, b) => a.id - b.id);
  for (const city of cities) {
    if (inDisorder(state, content, city)) continue;
    city.culture += culturePerTurn(state, content, city);
    const radius = borderRadiusFor(content, city.culture);
    if (radius > city.borderRadius) {
      city.borderRadius = radius;
      events.push({ t: 'BordersExpanded', cityId: city.id, name: city.name, radius });
    }
  }
  return events;
}

/**
 * Work out who owns every tile from the cities as they stand.
 *
 * Returns the ids of cities that were working a tile now owned by someone else, in
 * ascending order; the caller lets their governors choose again.
 */
export function recomputeBorders(state: GameState, content: Content): Id[] {
  void content;
  const map = state.map;
  const n = tileCount(map);
  const owner = new Int16Array(n).fill(NONE);
  // The best claim on each tile so far: its tier (centre 3, first ring 2, beyond 1),
  // and its influence as a fraction weight / distance², compared exactly by
  // cross-multiplying so no rounding can ever decide a frontier.
  const tier = new Int8Array(n);
  const weight = new Float64Array(n);
  const dist2 = new Int32Array(n);

  for (const city of Object.values(state.cities).sort((a, b) => a.id - b.id)) {
    const centre = indexToHex(map, tileIndex(map, city.col, city.row));
    const w = city.culture + 1;
    for (const h of hexSpiral(centre, city.borderRadius)) {
      const index = hexIndex(map, h);
      if (index === -1) continue;
      const d = hexDistance(centre, h);
      const t = d === 0 ? 3 : d === 1 ? 2 : 1;
      const d2 = Math.max(1, d * d);
      let take = false;
      if (tier[index] === 0 || t > tier[index]!) take = true;
      else if (t === tier[index]) {
        const cmp = w * dist2[index]! - weight[index]! * d2;
        // A tie goes to whoever holds the tile already: frontiers do not flicker.
        take = cmp > 0 || (cmp === 0 && city.owner === map.owner[index] && owner[index] !== map.owner[index]);
      }
      if (take) {
        tier[index] = t;
        weight[index] = w;
        dist2[index] = d2;
        owner[index] = city.owner;
      }
    }
  }

  const affected = new Set<Id>();
  for (let i = 0; i < n; i++) {
    if (owner[i] === map.owner[i]) continue;
    map.owner[i] = owner[i]!;
    const worker = map.workedBy[i]!;
    if (worker === NONE) continue;
    const city = state.cities[worker];
    if (city && owner[i] !== NONE && owner[i] !== city.owner) affected.add(city.id);
  }
  return [...affected].sort((a, b) => a - b);
}
